export function throttle<T extends (...args: any[]) => any>(
  fn: T,
  delay: number = 300
): (...args: Parameters<T>) => void {
  let timer: NodeJS.Timeout | null = null

  return function (this: any, ...args: Parameters<T>) {
    if (timer) return

    timer = setTimeout(() => {
      fn.apply(this, args)
      timer = null
    }, delay)
  }
}

export function debounce<T extends (...args: any[]) => any>(
  fn: T,
  delay: number = 300
): (...args: Parameters<T>) => void {
  let timer: NodeJS.Timeout | null = null

  return function (this: any, ...args: Parameters<T>) {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
      fn.apply(this, args)
    }, delay)
  }
}

/**
 * 将数组转换成树形结构
 * @param array 源数组
 * @param options 配置选项
 * @returns 树形结构数组
 */
export function arrayToTree<T extends Record<string, any>>(
  array: T[],
  options: {
    id?: string // ID字段名
    parentId?: string // 父ID字段名
    children?: string // 子节点字段名
    rootParentId?: any // 根节点的父ID值
  } = {}
): T[] {
  // 设置默认选项
  const defaultOptions = {
    id: 'id',
    parentId: 'parent_id',
    children: 'children',
    rootParentId: 0
  }

  // 合并选项
  const opts = { ...defaultOptions, ...options }
  const { id, parentId, children, rootParentId } = opts

  // 创建节点映射，用于快速查找
  const nodeMap = new Map<any, T>()

  // 构建节点映射
  for (const item of array) {
    // 确保每个节点都有子节点数组
    ;(item as any)[children] = []
    nodeMap.set(item[id], item)
  }

  // 构建树结构
  const result: T[] = []

  for (const item of array) {
    const pid = item[parentId]

    if (pid === rootParentId) {
      // 根节点
      result.push(item)
    } else {
      // 子节点，添加到父节点的children数组中
      const parent = nodeMap.get(pid)
      if (parent) {
        ;(parent as any)[children].push(item)
      } else {
        // 找不到父节点，视为顶级节点
        result.push(item)
      }
    }
  }

  return result
}

// 传入函数  遍历树形数据 找到特定节点
export function findTreeNode<T extends Record<string, any>>(tree: T[] | undefined, fn: (node: T) => boolean): T | null {
  if (!tree || !Array.isArray(tree)) {
    return null
  }

  for (const node of tree) {
    if (fn(node)) {
      return node
    }

    if (node.children && Array.isArray(node.children)) {
      const result = findTreeNode(node.children, fn)
      if (result) {
        return result
      }
    }
  }
  return null
}

/**
 * 过滤树形结构数据
 *
 * 该函数递归地过滤树形结构，只保留符合条件的节点及其子节点。
 * 如果父节点不符合条件，即使其子节点符合条件，也会被过滤掉。
 *
 * @param tree 要过滤的树形数据数组
 * @param fn 过滤函数，返回true表示保留节点，false表示过滤掉节点
 * @param childrenKey 子节点数组的属性名，默认为'children'
 * @returns 过滤后的新树形数据（不会修改原始数据）
 *
 * @example
 * // 过滤出name包含'test'的节点及其子节点
 * const filteredTree = filterTree(tree, node => node.name.includes('test'));
 *
 * // 使用自定义的子节点属性名
 * const filteredTree = filterTree(tree, node => node.visible, 'subItems');
 */
export function filterTree<T extends Record<string, any>>(
  tree: T[],
  fn: (node: T) => boolean,
  childrenKey: string = 'children'
): T[] {
  return tree
    .filter((node) => {
      // 首先检查节点本身是否符合条件
      return fn(node)
    })
    .map((node) => {
      // 创建节点的浅拷贝，避免修改原始数据
      const copy = { ...node }

      // 如果节点有子节点，递归过滤子节点
      if (copy[childrenKey] && Array.isArray(copy[childrenKey])) {
        const filtered = filterTree<T>(copy[childrenKey] as T[], fn, childrenKey)
        // 更新拷贝节点的子节点
        ;(copy as any)[childrenKey] = filtered
      }

      return copy
    })
}

/**
 * 角度转弧度
 * @param deg 角度值
 * @returns 弧度值
 */
export function deg2rad(deg: number): number {
  return deg * (Math.PI / 180)
}

/**
 * 计算两个经纬度点之间的距离（使用Vincenty算法）
 * 这是一个精确的地理距离计算方法，考虑了地球椭球体形状
 * @param lat1 第一个点的纬度
 * @param lon1 第一个点的经度
 * @param lat2 第二个点的纬度
 * @param lon2 第二个点的经度
 * @returns 两点之间的距离，单位km
 */
export function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  // 处理相同点的情况
  if (lat1 === lat2 && lon1 === lon2) {
    return 0
  }

  // 转换为弧度
  const radLat1 = deg2rad(lat1)
  const radLon1 = deg2rad(lon1)
  const radLat2 = deg2rad(lat2)
  const radLon2 = deg2rad(lon2)

  // WGS-84椭球参数
  const a = 6378137.0 // 赤道半径，单位米
  const f = 1 / 298.257223563 // 扁率
  const b = a * (1 - f) // 极半径

  const L = radLon2 - radLon1 // 经度差

  // 计算辅助变量
  const tanU1 = (1 - f) * Math.tan(radLat1)
  const cosU1 = 1 / Math.sqrt(1 + tanU1 * tanU1)
  const sinU1 = tanU1 * cosU1

  const tanU2 = (1 - f) * Math.tan(radLat2)
  const cosU2 = 1 / Math.sqrt(1 + tanU2 * tanU2)
  const sinU2 = tanU2 * cosU2

  let lambda = L
  let lambdaP
  let iterLimit = 100
  let sinLambda, cosLambda, sinSigma, cosSigma, sigma, sinAlpha, cosSqAlpha, cos2SigmaM

  do {
    sinLambda = Math.sin(lambda)
    cosLambda = Math.cos(lambda)

    sinSigma = Math.sqrt(
      cosU2 * sinLambda * (cosU2 * sinLambda) +
        (cosU1 * sinU2 - sinU1 * cosU2 * cosLambda) * (cosU1 * sinU2 - sinU1 * cosU2 * cosLambda)
    )

    // 如果两点重合，则距离为0
    if (sinSigma === 0) {
      return 0
    }

    cosSigma = sinU1 * sinU2 + cosU1 * cosU2 * cosLambda
    sigma = Math.atan2(sinSigma, cosSigma)
    sinAlpha = (cosU1 * cosU2 * sinLambda) / sinSigma
    cosSqAlpha = 1 - sinAlpha * sinAlpha

    // 避免除以零错误
    cos2SigmaM = cosSigma - (2 * sinU1 * sinU2) / cosSqAlpha
    if (isNaN(cos2SigmaM)) {
      cos2SigmaM = 0
    }

    const C = (f / 16) * cosSqAlpha * (4 + f * (4 - 3 * cosSqAlpha))
    lambdaP = lambda
    lambda =
      L +
      (1 - C) * f * sinAlpha * (sigma + C * sinSigma * (cos2SigmaM + C * cosSigma * (-1 + 2 * cos2SigmaM * cos2SigmaM)))
  } while (Math.abs(lambda - lambdaP) > 1e-12 && --iterLimit > 0)

  // 如果不收敛，则使用Haversine公式作为备用方案
  if (iterLimit === 0) {
    console.warn('Vincenty算法不收敛，使用Haversine公式作为备用')
    const R = 6371 // 地球半径，单位km
    const dLat = radLat2 - radLat1
    const dLon = radLon2 - radLon1
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(radLat1) * Math.cos(radLat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c
  }

  const uSq = (cosSqAlpha * (a * a - b * b)) / (b * b)
  const A = 1 + (uSq / 16384) * (4096 + uSq * (-768 + uSq * (320 - 175 * uSq)))
  const B = (uSq / 1024) * (256 + uSq * (-128 + uSq * (74 - 47 * uSq)))

  const deltaSigma =
    B *
    sinSigma *
    (cos2SigmaM +
      (B / 4) *
        (cosSigma * (-1 + 2 * cos2SigmaM * cos2SigmaM) -
          (B / 6) * cos2SigmaM * (-3 + 4 * sinSigma * sinSigma) * (-3 + 4 * cos2SigmaM * cos2SigmaM)))

  const distance = (b * A * (sigma - deltaSigma)) / 1000 // 转换为公里

  return distance
}

/**
 * 获取当前位置
 * @returns Promise 包含当前位置的经纬度
 */
export function getCurrentLocation(): Promise<{ latitude: number; longitude: number }> {
  return new Promise((resolve, reject) => {
    uni.getLocation({
      type: 'gcj02',
      success: (res) => {
        resolve({
          latitude: res.latitude,
          longitude: res.longitude
        })
      },
      fail: (err) => {
        console.error('获取位置失败', err)
        reject(err)
      }
    })
  })
}

/**
 * 根据当前位置获取最近的园区
 * @param parks 园区列表
 * @returns 最近的园区，如果获取位置失败则返回第一个园区
 */
export async function getNearestPark(parks: any[]): Promise<any> {
  if (!parks || parks.length === 0) {
    return null
  }

  try {
    // 获取当前位置
    const currentLocation = await getCurrentLocation()
    console.log('当前位置', currentLocation)

    // 为每个园区计算距离
    interface ParkWithDistance {
      id: number
      name: string
      longitude: string
      latitude: string
      distance: number
      [key: string]: any
    }

    const parksWithDistance = parks.map((park: any): ParkWithDistance => {
      const distance = calculateDistance(
        currentLocation.latitude,
        currentLocation.longitude,
        parseFloat(park.latitude),
        parseFloat(park.longitude)
      )
      return { ...park, distance }
    })

    // 按距离排序
    parksWithDistance.sort((a: ParkWithDistance, b: ParkWithDistance) => a.distance - b.distance)
    console.log('最近园区', parksWithDistance)
    // 返回最近的园区
    return parksWithDistance[0]
  } catch (error) {
    console.error('获取位置或计算距离失败，使用默认园区', error)
    // 获取位置失败，返回第一个园区
    return parks[0]
  }
}

export const calculateTimeTextRange = (days: any) => {
  const now = new Date()
  const pastDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000)
  const startDate = formatDateTime(pastDate)
  const endDate = formatDateTime(now, true)
  return [startDate, endDate]
}
export const calculateTimeRange = (days: number) => {
  const now = new Date()
  // 获取结束时间的 10 位时间戳，23:59:59 对应的时间戳
  const endDate = Math.floor(new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59).getTime() / 1000)
  const pastDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000)
  // 获取开始时间的 10 位时间戳，00:00:00 对应的时间戳
  const startDate = Math.floor(
    new Date(pastDate.getFullYear(), pastDate.getMonth(), pastDate.getDate(), 0, 0, 0).getTime() / 1000
  )
  return [startDate, endDate]
}
// 格式化日期时间
const formatDateTime = (date: any, show = false) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  if (show) {
    return `${year}-${month}-${day} 23:59:59`
  } else {
    return `${year}-${month}-${day}`
  }
}

/**
 * 时间戳转时间格式
 * @param time 时间戳，支持 10 位和 13 位
 * @param flag 格式化标志，1: YYYY-MM-DD; 2: hh:mm; 3: MM-DD hh:mm; 4: { year, month }; 默认: YYYY-MM-DD hh:mm:ss
 * @returns 格式化后的时间字符串或对象
 */
export const timeToDate = (time: number | string, flag = 1) => {
  let date: Date // 明确 date 为 Date 类型
  if (String(time).length === 13) {
    date = new Date(Number(time))
  } else {
    date = new Date(Number(time) * 1000)
  }
  const Y = date.getFullYear()
  const M = String(date.getMonth() + 1).padStart(2, '0')
  const D = String(date.getDate()).padStart(2, '0')
  const h = String(date.getHours()).padStart(2, '0')
  const m = String(date.getMinutes()).padStart(2, '0')
  const s = String(date.getSeconds()).padStart(2, '0')

  switch (flag) {
    case 1:
      return `${Y}-${M}-${D}`
    case 2:
      return `${h}:${m}`
    case 3:
      return `${M}-${D} ${h}:${m}`
    case 4:
      return { year: Y, month: date.getMonth() + 1 }
    case 5:
      return `${Y}-${M}-${D} ${h}:${m}`
    default:
      return `${Y}-${M}-${D} ${h}:${m}:${s}`
  }
}
